// Shared classification helpers for the VISIT_VIEWER module (frontend).
// Backend twin: backend/utils/visitsClassification.js — keep in sync.
//
// Visits are tagged backend-side (isDeleted / specialType / didNotHappen); the
// frontend uses these predicates as the single source of truth for "does this
// visit count toward analytics", instead of scattered status checks.

/**
 * The visit status vocabulary. Statuses used to be numbers (3, 2, 4, -1, 1) and
 * are now strings, mirroring DWH_PREPROD.DWH.FCT_VISITS.VISIT_STATUS. Two are
 * derived by the backend and never appear in the warehouse: MISSED (a planned
 * visit whose date has passed) and NOT_DONE (a completed visit the promoter
 * declared did not happen).
 *
 * This is the ONLY place statuses, labels and colors are defined. They used to be
 * restated in six files, which had already drifted: "Non effectuée" was purple in
 * the legend and amber on the calendar cards, and the calendar's sort order table
 * omitted "Manquée" entirely — `order[status]` returned undefined, so the
 * comparator produced NaN and those visits sorted arbitrarily.
 */
export const VISIT_STATUS = {
  COMPLETED: 'completed',
  PLANNED: 'planned',
  MISSED: 'missed',
  NOT_DONE: 'not-done',
  CANCELLED: 'cancelled',
  PENDING: 'pending',
  ABSENT: 'absent',
  UNKNOWN: 'unknown',
};

/**
 * Label, color and sort rank per status.
 *
 * `admin` marks the statuses that are analytics exclusions — shown to admins as
 * separate signals, hidden from everyone else. `outOfScope` marks statuses this
 * module never serves: PENDING is filtered out in SQL, and ABSENT only ever
 * occurs on the GI source. They are listed so such a row is recognized and set
 * aside deliberately rather than rendered as "Inconnue".
 */
export const VISIT_STATUS_META = {
  [VISIT_STATUS.COMPLETED]: { label: 'Effectuée',     color: '#10B981', rank: 1 },
  [VISIT_STATUS.PLANNED]:   { label: 'Prévue',        color: '#3B82F6', rank: 2 },
  [VISIT_STATUS.MISSED]:    { label: 'Manquée',       color: '#F97316', rank: 3 },
  [VISIT_STATUS.NOT_DONE]:  { label: 'Non effectuée', color: '#9b59b6', rank: 4, admin: true },
  [VISIT_STATUS.CANCELLED]: { label: 'Supprimée',     color: '#EF4444', rank: 5, admin: true },
  [VISIT_STATUS.PENDING]:   { label: 'Brouillon',     color: '#95A5A6', rank: 6, admin: true, outOfScope: true },
  [VISIT_STATUS.ABSENT]:    { label: 'Absent',        color: '#9CA3AF', rank: 7, admin: true, outOfScope: true },
  [VISIT_STATUS.UNKNOWN]:   { label: 'Inconnue',      color: '#9CA3AF', rank: 8 },
};

const UNKNOWN_META = VISIT_STATUS_META[VISIT_STATUS.UNKNOWN];

export const statusMeta = (status) => VISIT_STATUS_META[status] || UNKNOWN_META;
export const statusLabel = (status) => statusMeta(status).label;
export const statusColor = (status) => statusMeta(status).color;
// Unrecognized statuses sort last instead of poisoning the comparator with NaN.
export const statusRank = (status) => (VISIT_STATUS_META[status] || { rank: 99 }).rank;
export const isKnownVisitStatus = (status) => Object.hasOwn(VISIT_STATUS_META, status);

/** Statuses offered in a status filter, in display order. */
export const selectableStatuses = ({ isAdmin = false } = {}) =>
  Object.entries(VISIT_STATUS_META)
    .filter(([, meta]) => !meta.outOfScope && (isAdmin || !meta.admin))
    .sort((a, b) => a[1].rank - b[1].rank)
    .map(([value, meta]) => ({ value, label: meta.label }));

// Synthetic status for a completed visit the promoter declared did NOT happen.
// Kept as a named export because it reads better than the raw string at call sites.
export const STATUS_NOT_DONE = VISIT_STATUS.NOT_DONE;

// Curated dictionary of placeholder / non-store "visits". CONSERVATIVE matching:
// `exact` = full-string equality (normalized), `prefix` = startsWith with a real
// boundary (e.g. 'BOX ' not 'BOX'). NEVER substring. Add a family in one line.
export const SPECIAL_VISIT_RULES = [
  { type: 'ADMIN',            exact: ['ADMIN'],        prefix: ['ADMIN ', 'VISITE ADMIN ', 'OUT STORE ADMIN'] },
  { type: 'BOX',              exact: ['VISITE BOX'],   prefix: ['BOX ', 'VISITE BOX '] },
  { type: 'EVENT',                                     prefix: ['EVENT ', 'OUT STORE - EVENEMENTIEL'] },
  { type: 'FORMATION',                                 prefix: ['OUT STORE FORMATION', 'FORMATION '] },
  { type: 'CREATION_CONTENU',                          prefix: ['OUT STORE CREATION CONTENU'] },
  { type: 'RDV',                                       prefix: ['RDV BUSINESS'] },
  { type: 'TEST',             exact: ['MAGASIN TEST'] },
  // Trade shows / external training. Every observed label starting with 'SALON ' is a
  // numbered placeholder (Salon 1 … Salon 11, 206 visits); a real store always carries
  // its brand first ('DARTY SALON DE PROVENCE'), which a prefix rule cannot reach.
  // 'VISITE SALON / FORMATION EXTERNE N' is 290 more visits on the same family.
  { type: 'SALON',                                     prefix: ['SALON ', 'VISITE SALON'] },
  { type: 'CONGES',                                    prefix: ['CONGE', 'CONGÉ'] }, // not seen in data yet, future-proof
];

// Human labels for the admin "special visits" widget.
export const SPECIAL_TYPE_LABELS = {
  ADMIN: 'Admin',
  BOX: 'Box',
  EVENT: 'Événement',
  FORMATION: 'Formation',
  CREATION_CONTENU: 'Création contenu',
  RDV: 'RDV business',
  TEST: 'Test',
  SALON: 'Salon / formation externe',
  CONGES: 'Congés',
  // Ni point d'intervention ni enseigne : aucune boutique rattachée, donc pas une
  // visite magasin. 0 ligne sur TeamHaven, 316 sur Sidely au 14/09/2026.
  NO_STORE: 'Sans magasin',
};

const normalize = (s) => (typeof s === 'string' ? s.trim().toUpperCase() : '');
const up = (s) => s.toUpperCase();

/**
 * Classify a visit as a special/placeholder one from the labels that can carry the
 * evidence. Returns the `type` (e.g. 'ADMIN') or null for a real store visit.
 *
 * Accepts SEVERAL labels because the giveaway is not always in the same field.
 * Testing the intervention point alone let 3 815 TH rows through, across 8
 * projects and 107 "stores": their shop sign is ADMIN (or 'ADMIN FNAC') while the
 * intervention point carries a free-text activity instead of a store —
 * 'TRAINING OCCITANIE', 'RELEVE KILOMETRIQUE HK LYON',
 * 'ACCOMPAGNEMENT CMM CENTRE EST'. They were counted as store visits and dragged
 * the duration averages with them.
 *
 * Rules are evaluated in dictionary order, each against every label, so rule
 * priority is unchanged and only the evidence base widens.
 */
export const classifySpecialVisit = (...labels) => {
  const names = labels.map(normalize).filter(Boolean);
  if (!names.length) return 'NO_STORE';

  for (const rule of SPECIAL_VISIT_RULES) {
    for (const name of names) {
      if (rule.exact && rule.exact.some((e) => name === up(e))) return rule.type;
      if (rule.prefix && rule.prefix.some((p) => name.startsWith(up(p)))) return rule.type;
    }
  }
  return null;
};

// A visit counts toward analytics iff it is a real store visit, not deleted,
// not explicitly "did not happen", and its status is one we understand. The last
// clause is what stops a new warehouse value from being counted by accident.
export const isCountableVisit = (v) =>
  !v.isDeleted && !v.specialType && !v.didNotHappen && isKnownVisitStatus(v.status);

// Effectively completed = realized AND countable (the gate override demotes it).
export const isCompletedVisit = (v) => v.status === VISIT_STATUS.COMPLETED && isCountableVisit(v);
